<script setup lang="ts">
import {
  instanceOfMvTherapySystemSetupDto,
  type MedicalSystemRtSystemDto,
  ParameterCombinationTableDto,
  type QaEquipmentRtSystemDto,
  type TaskItemAreaDto,
  TaskTypeDescription,
} from '@/api/generator';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import Notification from '@/components/notifications/Notification.vue';
import { NotificationMessage } from '@/components/notifications/types';
import { truthy } from '@/utils/helper.ts';
import { useToggle } from '@vueuse/core';
import { useField, useFieldValue, useSetFieldValue } from 'vee-validate';
import { computed, ref, toRef, useTemplateRef } from 'vue';
import EntityScopeSelection from './EntityScopeSelection.vue';
import Evaluations from './Evaluations.vue';
import EvaluationsManualTasks from './EvaluationsManualTasks.vue';
import FormAddButton from './FormAddButton.vue';
import Instructions from './Instructions.vue';
import QASection from './QASection.vue';
import BeamShapingConfiguration from '@/views/tasks/components/beamShaping/BeamShapingConfiguration.vue';
import SetupParameterSelectDialog from './SetupParameterSelectDialog.vue';
import SetupCombinations from './SetupCombinations.vue';
import SingleSetupParameters from './SingleSetupParameters.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import BeamQualityContainer from './BeamQualityContainer.vue';

interface AreaFormProps {
  areaIndex?: number;
  medicalSystems: MedicalSystemRtSystemDto[];
  qaEquipmentSystemEntities: QaEquipmentRtSystemDto[];
  notification?: NotificationMessage;
}

const props = defineProps<AreaFormProps>();

const mode = useFormMode();

const name = computed<string>(() => `taskItemAreas[${props.areaIndex}]`);

// switch by task type
const taskType = useFieldValue<TaskTypeDescription>('taskType');

const { value: targetArea } = useField<TaskItemAreaDto | undefined>(name);

const setBeamQualitiesEnabledState = useSetFieldValue<boolean>(
  toRef(() => `${name.value}.medicalSystemSetup.beamQualityContainer.isEnabled`),
);

const combinationTable = ref<ParameterCombinationTableDto | undefined>(undefined);

const areas = useFieldValue<TaskItemAreaDto[]>('taskItemAreas');

const excludedIds = computed(() =>
  areas.value.map((area) => area.medicalSystemSetup?.targetSystemEntityId).filter(truthy),
);

const [isParameterDialogOpen, toggleParameterDialog] = useToggle(false);

function disableBeamShapingsContainer() {
  const beamShapingContainer = instanceOfMvTherapySystemSetupDto(
    targetArea.value?.medicalSystemSetup,
  )
    ? targetArea.value?.medicalSystemSetup.beamShapingContainer
    : undefined;

  if (beamShapingContainer) {
    beamShapingContainer.selectedBeamShapings = [];
    beamShapingContainer.isEnabled = false;
  }
}

const medicalSystemIsSelected = computed(
  () => !!targetArea.value?.medicalSystemSetup?.targetSystemEntityId,
);

const singleSetupParametersSelected = computed(
  () =>
    targetArea.value?.medicalSystemSetup?.singleSetupParameters?.filter(
      (parameter) => parameter.isEnabled,
    ).length !== 0,
);

const beamQualitiesEnabled = computed<boolean>(() => {
  const medicalSystemSetup = targetArea.value?.medicalSystemSetup;

  const beamQualityContainer = instanceOfMvTherapySystemSetupDto(medicalSystemSetup)
    ? medicalSystemSetup.beamQualityContainer
    : undefined;

  return (
    !!beamQualityContainer?.isEnabled &&
    !![...beamQualityContainer.beamQualitiesPhoton, ...beamQualityContainer.beamQualitiesElectron]
      .length
  );
});

const beamShapingEnabled = computed(
  () =>
    instanceOfMvTherapySystemSetupDto(targetArea.value?.medicalSystemSetup) &&
    targetArea.value?.medicalSystemSetup?.beamShapingContainer?.isEnabled,
);

const hasAnyEvaluationParameters = computed(() => {
  return !!targetArea.value?.evaluationScopes?.some(
    (scope) =>
      scope.isEnabled && !!scope.evaluationParameters?.some((parameter) => parameter.isEnabled),
  );
});

const entityScopeRef = useTemplateRef<InstanceType<typeof EntityScopeSelection>>('entityScope');

const setupParameterExpansionPanelModel = ref<number | null>(0);
</script>

<template>
  <div class="area-form">
    <div class="d-flex flex-column ga-6">
      <!-- Medical system -->
      <v-card class="mt-3 container-level-1">
        <v-card-item>
          <v-card-title class="d-flex align-center">
            <h5>{{ $t('MedicalSystemEntity_one') }}</h5>
            <VSpacer />
            <FormAddButton
              v-if="mode !== 'details'"
              :disabled="medicalSystems.length === excludedIds.length"
              :label="'SelectMedicalSystem'"
              @click="entityScopeRef?.toggleModal()"
          /></v-card-title>
        </v-card-item>

        <v-card-text>
          <!-- Medical system -->
          <EntityScopeSelection
            v-if="medicalSystems"
            ref="entityScope"
            :name="`${name}.medicalSystemSetup`"
            :taskType="taskType"
            :excludedIds="excludedIds"
            :options="medicalSystems"
            :class="targetArea?.medicalSystemSetup?.targetSystemEntityId ? 'mb-6' : ''"
          />

          <InlineValidationError :name="`${name}.medicalSystemSetup`" class="mb-2 mt-1" />

          <p
            v-if="!medicalSystemIsSelected"
            class="text-body-1 text-primary-lighten-2 font-weight-semibold mb-2"
          >
            {{ $t('SelectMedicalSystemForTaskPerformance') }}
          </p>

          <Notification v-if="notification" :value="notification" class="pa-4" />

          <!-- Medical system setup parameters -->
          <SetupParameterSelectDialog
            v-model="isParameterDialogOpen"
            :name="`${name}.medicalSystemSetup`"
            :taskType="taskType"
          />

          <v-card
            v-if="
              targetArea?.medicalSystemSetup &&
              (mode !== 'details' ||
                singleSetupParametersSelected ||
                beamQualitiesEnabled ||
                beamShapingEnabled)
            "
            class="container-level-2"
          >
            <v-card-item>
              <v-card-title class="d-flex align-center">
                <h5>{{ $t('SetupParameters') }}</h5>

                <template
                  v-if="
                    (targetArea.medicalSystemSetup &&
                      instanceOfMvTherapySystemSetupDto(targetArea.medicalSystemSetup) &&
                      (targetArea.medicalSystemSetup?.beamQualityContainer?.isEnabled ||
                        targetArea.medicalSystemSetup.beamShapingContainer?.isEnabled)) ||
                    targetArea?.medicalSystemSetup?.singleSetupParameters?.length
                  "
                >
                  <div class="d-flex flex-grow-1" />

                  <FormAddButton
                    v-if="mode !== 'details' && taskType === 'ManualTask'"
                    :label="'AddSetupParameter'"
                    class="mr-6"
                    @click.stop="
                      () => {
                        toggleParameterDialog(true);
                        setupParameterExpansionPanelModel = 0;
                      }
                    "
                  />
                </template>
              </v-card-title>

              <v-card-text>
                <div class="d-flex flex-column ga-6 mt-4">
                  <!-- BeamQualities -->
                  <v-card v-if="beamQualitiesEnabled" class="beam-qualities">
                    <v-card-item class="pa-0">
                      <v-card-title class="parameter-title">
                        <div class="d-flex flex-grow-1 align-center">
                          <h6 class="text-primary-darken-2 ma-0">
                            {{ $t('BeamQualityParameters') }}
                          </h6>

                          <VSpacer />

                          <VSpacer />

                          <PtwDeleteButton
                            v-if="mode !== 'details' && taskType === 'ManualTask'"
                            @delete="setBeamQualitiesEnabledState(false)"
                          />
                        </div>
                      </v-card-title>
                    </v-card-item>

                    <v-card-text>
                      <BeamQualityContainer
                        :name="`${name}.medicalSystemSetup.beamQualityContainer`"
                      />
                    </v-card-text>
                  </v-card>

                  <!-- Beam shaping -->
                  <v-card v-if="beamShapingEnabled" class="beam-shaping container-level-3">
                    <v-card-item>
                      <v-card-title class="parameter-title">
                        <h6>
                          {{ $t('BeamShapingParameters') }}
                        </h6>

                        <VSpacer />

                        <PtwDeleteButton
                          v-if="mode !== 'details' && taskType === 'ManualTask'"
                          @click="disableBeamShapingsContainer()"
                        />
                      </v-card-title>
                    </v-card-item>

                    <v-card-text>
                      <InlineValidationError
                        class="inline-error dense ml-4"
                        ignoreTouched
                        :name="`${name}.medicalSystemSetup.beamShapingContainer.selectedBeamShapings`"
                      />

                      <BeamShapingConfiguration
                        :name="`${name}.medicalSystemSetup.beamShapingContainer.selectedBeamShapings`"
                        :taskType="taskType"
                        :medicalSystemSetup="targetArea.medicalSystemSetup"
                        :containerEnabled="beamShapingEnabled"
                      />
                    </v-card-text>
                  </v-card>

                  <!-- Single setup parameters -->
                  <SingleSetupParameters
                    :targetArea="targetArea"
                    :areaIndex="areaIndex"
                    :name="name"
                    :singleSetupParametersSelected="singleSetupParametersSelected"
                  />

                  <div
                    v-if="
                      !singleSetupParametersSelected && !beamQualitiesEnabled && !beamShapingEnabled
                    "
                    class="d-flex align-center justify-center ma-6"
                  >
                    <p>{{ $t('NoSingleSetupParametersAddedYet') }}</p>
                  </div>
                </div>
              </v-card-text>
            </v-card-item>
          </v-card>
        </v-card-text>
      </v-card>

      <!-- Dosimetry & QA equipment -->
      <QASection
        :name="`${name}.qaEquipmentSetups`"
        :taskType="taskType"
        :medicalSystemSelected="!!targetArea?.medicalSystemSetup?.targetSystemEntityId"
        class="container-level-1"
      />

      <v-card class="container-level-1">
        <v-card-item>
          <v-card-title>
            <h5>{{ $t('SetupParameterCombinations') }}</h5>
          </v-card-title>
        </v-card-item>

        <v-card-text>
          <SetupCombinations
            v-if="targetArea"
            v-model:combinationTable="combinationTable"
            :taskType="taskType"
            :name="`taskItemAreas[${areaIndex}]`"
            :area="targetArea"
            :medicalSystemSetup="
              targetArea?.medicalSystemSetup ? targetArea?.medicalSystemSetup : undefined
            "
            :qaEquipmentSetups="targetArea?.qaEquipmentSetups || []"
            :medicalSystem="
              medicalSystems.find(
                (medicalSystem) =>
                  medicalSystem.id === targetArea?.medicalSystemSetup?.targetSystemEntityId,
              )
            "
            :qaEquipmentSystemEntities="
              qaEquipmentSystemEntities.filter((qaEquipmentSystemEntity) =>
                targetArea?.qaEquipmentSetups?.[0]?.supportSystemEntityIds?.includes(
                  qaEquipmentSystemEntity.id,
                ),
              )
            "
          />
        </v-card-text>
      </v-card>

      <v-card class="container-level-1">
        <v-card-item>
          <v-card-title>
            <h5>{{ $t('Evaluation_other') }}</h5>
          </v-card-title>
        </v-card-item>

        <v-card-text>
          <Evaluations
            v-if="taskType === 'BeamConstancyCheck'"
            :name="`${name}.evaluationScopes`"
            :targetSystemId="targetArea?.medicalSystemSetup?.targetSystemEntityId"
            :combinationTable="combinationTable"
          />

          <EvaluationsManualTasks
            v-else-if="taskType === 'ManualTask'"
            :name="`${name}.evaluationScopes[0]`"
            :mode="mode"
            :targetSystemId="targetArea?.medicalSystemSetup?.targetSystemEntityId"
          />

          <p
            v-if="!hasAnyEvaluationParameters"
            class="text-body-1 text-primary-lighten-2 font-weight-semibold"
          >
            {{ $t('SelectQuantitiesOrEnumerationsForTaskPerformance') }}
          </p>
        </v-card-text>
      </v-card>

      <Instructions
        v-if="areaIndex !== undefined"
        class="container-level-1"
        :textAreaContent="targetArea?.instruction?.textAreaContent"
        :areaIndex="areaIndex"
        :mode="targetArea?.medicalSystemSetup?.targetSystemEntityId ? mode : 'details'"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Parameters
.parameter-container {
  .v-expansion-panel-title {
    background-color: rgb(var(--v-theme-primary-lighten-5));
  }
}

.parameter-title,
:deep(.parameter-title) {
  min-height: 32px;
  font-size: initial;
  background-color: rgb(var(--v-theme-primary-lighten-5));
  padding-left: 16px;
  padding-right: 16px;
  align-items: center;
  display: flex;
}

// Errors
.inline-error {
  &.dense {
    flex: 0 0 fit-content;
    :deep(.v-alert__content) {
      overflow: visible;
    }
  }
}

// Selection
:deep(.v-checkbox) {
  .v-input__details {
    display: none;
  }
  .v-selection-control {
    min-height: 0;
  }
}

// Table
td {
  padding-bottom: 0 !important;
}

// Expandables
.with-border {
  border: 1px solid rgb(var(--v-theme-primary-lighten-4)); /* Customize your border color, style, width */
  border-radius: 4px; /* Optional if you want rounded corners */
}
</style>
